import useRestNode from './useRestNode'
import useEnv from './useEnv'
import useBreadCrumbs from './useBreadCrumbs'
import { format } from 'date-fns'
import useUtils from './useUtils'
import useAppContext from './useAppContext'
import usePlan from './usePlan'
import useSearchService from '../services/useSearchService'
import useMarketingGroupService from '../services/useMarketingGroupService'
import useCareConcepts from './useCareConcepts'

const { clone } = useUtils()

export default (req) => {

  const { get } = useRestNode()
  const { mapboxPublicKey, mapboxSecretKey } = useEnv()

  const model = {
    mapboxToken: mapboxPublicKey,
    profile: {},
    breadCrumbs: {},
    isAuthenticated: false,
    context: {},
    cleanContext: {},
    criteriaContext: {},
    filterContext: {},
    sortContext: {},
    isInternalUser: false,
    hospAffContext: {
      latitude: '',
      longitude: '',
      zip: '',
    },
    languages: [],
    toHelpMeWithItems: null,
    searchResults: null,
    rawConcepts: [],
    loading: false,
    hasSearched: false,
    hideLandingPage: false,
    promotedServices: {},
    onlineClinics: [],
    careNetworkClinics: [],
    isLocalAreaTravelNetwork: false,
    benefitCategories: null,
    benefits: null,

    // for network switching, for example, 
    // Individual and Family medical plans for State of MN
    // which has marketing group Id "1", and includes:
    // [                                                                                                                
    //   { networkId: '1013', networkName: 'Individual Peak' },
    //   { networkId: '1020', networkName: 'Apex Individual ' },
    //   { networkId: '1051', networkName: 'Select ACO' },
    //   { networkId: '1134', networkName: 'Individual Cornerstone' }
    // ]
    networks: [],

    // the current Plan details used during the search for UI display purposes
    currentPlanName: null,
    currentPlanTierLabel: null,
    currentPlanIsTiered: null,  
    
    //The list of specialties currently available to the specialties filter.
    specialties: [],
    medicalEquipment: [],
    services:[],

    //The list of races and ethnicities available to Race & ethnicity filter
    races: [],
    ethnicities: [],   
    origins: [], 

    //The list of procedures currently available to the procedures filter.
    procedures: [],
    pills: [],
    recommendedBadgeType: null,
    betterChoiceContext: null,
    pageSize: 20,
    currentPage: 1, 
    
    env: process.env.SPRING_PROFILES_ACTIVE,

    //This is needed by to capture the name of the lat/long location.
    //It is only needed by the PDF report.
    searchCriteriaPlaceName: null,
    serverError: false,
    costEstimatesFailed: false,
    isVision: false
  }

  const buildProfile = () => {
    model.profile = req.profile
    model.isAuthenticated = model.profile ? model.profile.isAuthenticated : false
    model.isInternalUser = req.isInternal
  }

  const cleanContext = () => {
    return {
      networkId: '',
      networkIndex: 0,
      asOfDate: '',
      type: '',
      where: '',
      latLong: '',
      term: '',
      name: '',
      languageId: '',
      language: '',
      gender: '',
      distance: 20,
      sort: '',
      tier: '',
      hDistance: 20,
      groupNetworkName: '',
      //holds care network name and id
      careNetworkName: '',
      careNetworkId: '',
      //holds the label of the hospital affliations in the filters
      hLabel: '',
      hWhere: '',
      hLatLong: '',
      //holds the code of the hospital affliations in the filters
      hCode: '',
      //holds the code of the procedure selection in the filters
      code: '',
      //holds care concepts selection(s) in the filters.
      cc: [],
      // race & ethnicity
      races: [],
      ethnicities: [],
      // country of origin
      origins: [],
      // for network switching, which is based on a given marketing group Id
      marketingGroup: '',
      // benefit category
      category: '',
      pcOnly: '',
    }
  }

  const getNetworkId = (profile, context) => {
    const { getNetworkId } = usePlan(profile, context)
    return getNetworkId()
  }

  const buildContext = () => {
    if (model.profile) {
      const clean = cleanContext()
      model.cleanContext = clean
      const context = useAppContext(clone(clean), req)
      model.context= context

      if (!model.context.type) {
        const { isDentalOnly } = usePlan(model.profile, model.context)
        model.context.type = isDentalOnly() ? 'dentist' : 'doctor'
      }

      // convert asofdate to Date object for comparison
      // check if date passed in from url is a past date
      // if yes, replace the asofdate with today's date
      var today = new Date()
      var formattedDate = new Date(context.asOfDate)
      if (formattedDate < today) model.context.asOfDate = format(today, 'yyyy-MM-dd')

      //If the profile is not authenticated and has an asofDate, set it on the context if the asOfDate has not already been supplied on the URL.
      if (model.profile && !model.profile.isAuthenticated && !model.context.asOfDate && model.profile.asOfDate) {
        model.context.asOfDate = model.profile.asOfDate
      }
    }
  }

  const buildBreadCrumbs = async () => {
    if(model.profile) {
      const { buildBreadCrumbs } = useBreadCrumbs(model.profile, model.context)
      const breadCrumbs = await buildBreadCrumbs()
      model.breadCrumbs = breadCrumbs
    }
  }  

  /**
 * If the user is authenticated, do a geoLocation lookup to find their latLong so that we can default it.
 */
  const geoLocate = async () => { 
    const { geoLocate: fetchGeo } = useGeolocation()
    console.log('geoLocate called', geoLocate)
    //If the profile ( authenticated or not ) has an initialZipCode, pre-populate the geo coordinates.
    
    if (model.profile && model.profile.isAuthenticated && model.profile.initialZipCode) {
      if (model.context.latLong == '') {
        
        try {

          // troubleshooting logs
          console.log(`GEOLOCATE, zip:${model.profile.initialZipCode}`)

          // On OpenShift, a secret key is passed via env.
          // Default to non-prod public key for local development
          const accessToken = mapboxSecretKey || mapboxPublicKey
          const result = await fetchGeo(model.profile.initialZipCode, accessToken)
          console.log('GEOLOCATE', result)
          if (result) {
            //Pop the initialZipCode in the searchCriteriaPlaceName for PDF report purposes.
            model.searchCriteriaPlaceName = model.profile.initialZipCode
            model.context.latLong = `${result.coordinates[1]},${result.coordinates[0]}`
          }
        } catch (error) {
          model.context.latLong = '44.9778,-93.2654' // Default to Minneapolis, MN if error occurs
          console.log('Error in geoLocate: ' + error.message)
        }
      }
    }
  }

  const buildLanguages = async () => {
    const {  getPlan } = usePlan(model.profile, model.context)
    const plan = getPlan()
    const groupId = plan && plan.coverageInfo != null ? plan.coverageInfo.groupNumber : ''
    const asOfDate = model.context.asOfDate
    if(plan){
      const { networkServiceEndpoint } = useEnv()
      let result = {}
      try {
        const networkId = getNetworkId(model.profile, model.context)
        const url = `${networkServiceEndpoint}/languages/networks/${networkId}?employerGroupId=${groupId}&asof=${asOfDate}`        
        result = await get(url)
      } catch (error) {
        console.log(error)
      }
      //model.languages.value = result
      model.languages = result.value.items
    }
  }

  /**
   * Build dropdown opetions, which allows users 
   * to switch networks within a marketing group
   */
  const buildNetworks = async () =>  {
    if(model.profile && !model.profile.isAuthenticated && model.context.marketingGroup) {
      const {loadNetworkData} = useMarketingGroupService()
      let result = {}
      try {
        const marketingGroup = model.context.marketingGroup
        result = await loadNetworkData(marketingGroup)

        console.log("NETWORK DATA", result)

      } catch (error) {
        console.log(error)
      }

      model.networks = result
    }

  }

  const buildCareNetworkInfo = async () => {

    const { getNetworkId, getPlan } = usePlan(model.profile, model.context)
    const plan = getPlan()
    const groupId = plan &&  plan.coverageInfo != null ? plan.coverageInfo.groupNumber : ''
    const asOfDate = model.context.asOfDate
    if (plan) {    
      const networkId = getNetworkId()

      if (plan.isPrimaryCare && plan.primaryCareClinic && plan.primaryCareClinic.careNetwork) {
        if (!model.context.careNetworkId) {
          model.context.careNetworkName = plan.plan?.primaryCareClinic?.careNetwork ? plan.primaryCareClinic.careNetwork.name : ''
          model.context.careNetworkId = plan?.primaryCareClinic?.careNetwork ? plan.primaryCareClinic.careNetwork.id : ''
        }

        let result = {}
        try {
          const { networkServiceEndpoint } = useEnv()
          const url = `${networkServiceEndpoint}/care-networks/network/${networkId}?employerGroupId=${groupId}&asof=${asOfDate}`          
          result = await get(url)
        } catch (error) {
          console.log(error)
        }
        model.careNetworkClinics = result.value

        //dont' necessarily need to return anything, but useful for unit/functional tests.
        return result.value
      }
    }
  }

  const buildPromotedServices = async () => {
    const { getPlan } = usePlan(model.profile, model.context)
    const plan = getPlan()
    const asOfDate = model.context.asOfDate
    if(plan) {
      const networkId = plan.networkId
      const groupId = plan.coverageInfo != null ? plan.coverageInfo.groupNumber : ''

      if (networkId && groupId != '' && (model.profile.hasAnyPipeTradesCoverage || plan.isSmartCare)) {
        let result = {}
        try {
          const { locationServiceEndpoint } = useEnv()
          const url = `${locationServiceEndpoint}/search-promoted-locations/network/${networkId}?employerGroupId=${groupId}&asof=${asOfDate}`          
          //console.log(url, 'preferred')
          result = await get(url)
        } catch (error) {
          console.log(error)
        }
        model.promotedServices = result.value
        return result.value
      }
    }
  }

  /** 
   * Check to see if this is a monumentHealth network and group.  If it is, we need
   * to apply a workaround and set the isMedicalOnly attribute on the profile to true.  This
   * will cause the dental options on the home page and the 'type' selector to disappear.
   * This is currently a workaround to solve an acute situation with MonumentHealth.  A better
   * solution may emerge later in the find-care profile service.  See PD-2778
   */
    const applyMonumentHealthWorkaround = () => {
      if(model.profile) {
        const { isMonumentHealth } = useMonumentHealth(model.profile, model.context)
        model.profile.isMedicalOnly = isMonumentHealth.value
      }
    }

  const initialSearch = async () => {
    const { transFormCareConceptsForFilters } = useCareConcepts()
    const { getPlan } = usePlan(model.profile, model.context)
    const plan = getPlan()
    if (plan && model.context.latLong && model.context.type) {
      const { search } = useSearchService(model.profile, model.context)
      //console.log('Executing search')
      const searchResult = await search()
      const {
        value,
        recommendedBadgeType,
        betterChoiceContext,
        onlineClinics,
        isLocalAreaTravelNetwork,
        currentPlanName,
        currentPlanTierLabel,
        currentPlanIsTiered,
        benefitCategories,
        rawConcepts
      } = searchResult
      //console.log('SEARCH VALUE IS:', value)
      //console.log(onlineClinics, 'online clinics in index')
      model.hasSearched = true
      if (searchResult.status.toLowerCase() == 'ok') {
        model.recommendedBadgeType = recommendedBadgeType
        model.betterChoiceContext = betterChoiceContext
        model.searchResults = value
        model.onlineClinics = onlineClinics
        model.isLocalAreaTravelNetwork = isLocalAreaTravelNetwork
        model.currentPlanName = currentPlanName
        model.currentPlanIsTiered = currentPlanIsTiered
        model.currentPlanTierLabel = currentPlanTierLabel
        model.benefitCategories = benefitCategories
        model.rawConcepts = transFormCareConceptsForFilters(rawConcepts)
      } else {
        model.searchResults = []
      }
    }  
  }

  const build = async () => {
    buildProfile()
    buildContext()
    applyMonumentHealthWorkaround()

    // await Promise.all([ buildBreadCrumbs(), geoLocate(), buildLanguages(), buildCareNetworkInfo(), buildPromotedServices(), buildNetworks() ])
    await Promise.all([ buildBreadCrumbs(), geoLocate(), buildLanguages(), buildCareNetworkInfo(), buildPromotedServices(), buildNetworks()])
    model.criteriaContext = clone(model.context)
    model.filterContext = clone(model.context)
    model.sortContext = clone(model.context)
    await initialSearch()

    return model
  }

  const fns = { build, buildProfile, buildContext, buildCareNetworkInfo, buildPromotedServices, cleanContext } 
  return fns
}
